import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  Stack,
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  tableCellClasses,
  styled,
  TableBody,
  Button
} from '@mui/material';

import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { setLoadingEvent, setEvent } from '../../features/event/eventSlice';

import { doc, onSnapshot } from 'firebase/firestore';
import { store } from '../../config/firebase';
import { useColorMode } from '../../hooks/colorModeProvider';
import PoweredBy from '../ui/poweredBy';
import { getEventSectionsLeaderboard } from '../../features/leaderboard/leaderboardSlice';
import BSCircularProgress from '../ui/progress/circularProgress';
import SectionTotalsCard from '../ui/scoreboard/sectionTotalsCard';
import SectionScoreCard from '../ui/scoreboard/sectionScorecard';
import EmbeddedActiveGames from './activeGames';
import EmbeddedBracketGames from './bracketGames';
import EmbeddedTabButtons from './embeddedTabButtons';
import EmbeddedFindMe from './findMe';
import { buildFindMeEntries } from '../../helpers/findMe';
import { getBracketsByEventId } from '../../features/bracket/bracketSlice';
import SingleBracket from '../ui/bracket';
import { pickPrimaryBracket } from '../../helpers/bracketData';
import { GameType } from '../../features/gameFormat/gameFormatService';
import { getGameType } from '../../constants/gameConstants';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    borderBottom: 'none',
    fontSize: 12
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    borderBottom: 'none'
  }
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}
CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

const EmbeddedDetailedSectionsView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const event = useSelector((state) => state.event.event);
  const loading = useSelector((state) => state.event.loading);
  const brackets = useSelector((state) => state.bracket.brackets);
  // const [selectedBracket, setSelectedBracket] = useState(null);

  const loadingLeaderboard = useSelector((state) => state.leaderboard.loading);
  const sectionsLeaderboard = useSelector((state) => state.leaderboard.sectionLeaderboard);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [findMeTarget, setFindMeTarget] = useState(null);

  const [linkedEvents, setLinkedEvents] = useState([]);
  const hasBrackets = brackets.length > 0;
  const primaryBracket = useMemo(() => pickPrimaryBracket(brackets), [brackets]);
  const showLogTab = event.publishLogs && event.hasSections;
  const showScorecardTab = event.publishScorecard && event.hasSections;

  let nextTabIndex = 1;
  const bracketGamesTabIndex = hasBrackets ? nextTabIndex++ : null;
  const bracketTabIndex = hasBrackets ? nextTabIndex++ : null;
  const logTabIndex = showLogTab ? nextTabIndex++ : null;
  const scorecardTabIndex = showScorecardTab ? nextTabIndex++ : null;

  const tabs = [{ label: 'Active Games', value: 0 }];
  if (hasBrackets) {
    tabs.push({ label: 'Bracket Games', value: bracketGamesTabIndex });
    tabs.push({ label: 'Bracket', value: bracketTabIndex });
  }
  if (showLogTab) {
    tabs.push({ label: 'Log', value: logTabIndex });
  }
  if (showScorecardTab) {
    tabs.push({ label: 'Scorecard', value: scorecardTabIndex });
  }

  const findMeEntries = useMemo(
    () => buildFindMeEntries({ event, sectionsLeaderboard }),
    [event, sectionsLeaderboard]
  );

  // Keep the banner in step with live event data, but keep the selection token
  // so choosing the same player again still scrolls to them.
  const located = useMemo(
    () =>
      findMeTarget
        ? findMeEntries.find((entry) => entry.key === findMeTarget.key) || null
        : null,
    [findMeEntries, findMeTarget]
  );

  const handleFindMeLocate = (entry) => {
    setFindMeTarget((previous) => ({
      key: entry.key,
      token: (previous?.token || 0) + 1
    }));
  };

  const handleFindMeClear = () => setFindMeTarget(null);

  useEffect(() => {
    dispatch(getBracketsByEventId(event.id));
  }, [event.id, dispatch]);

  useEffect(() => {
    if (!event) {
      setLinkedEvents([]);
      return undefined;
    }

    const linkedIds = Array.isArray(event.linkedEvents) ? event.linkedEvents : [];
    if (linkedIds.length === 0) {
      setLinkedEvents([]);
      return undefined;
    }

    let isMounted = true;
    const nextLinkedEvents = [];
    const unsubscribes = linkedIds.map((linkedId) => {
      const docRef = doc(store, 'events', linkedId);
      return onSnapshot(docRef, (docSnap) => {
        if (!isMounted || !docSnap.exists()) {
          return;
        }
        const existingIndex = nextLinkedEvents.findIndex((item) => item.id === linkedId);
        const nextEvent = { id: docSnap.id, ...docSnap.data() };
        if (existingIndex >= 0) {
          nextLinkedEvents[existingIndex] = nextEvent;
        } else {
          nextLinkedEvents.push(nextEvent);
        }
        setLinkedEvents([...nextLinkedEvents]);
      });
    });

    return () => {
      isMounted = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [event]);

  useEffect(() => {
    if (event) {
      dispatch(getEventSectionsLeaderboard({ eventId: event.id }));
    }
  }, [dispatch, event]);

  // A new event has new teams, and leaving the Log tab unmounts the cards, so
  // the old highlight must not come back the next time the log is opened.
  useEffect(() => {
    setFindMeTarget(null);
  }, [event?.id]);

  useEffect(() => {
    if (selectedTab !== logTabIndex) {
      setFindMeTarget(null);
    }
  }, [selectedTab, logTabIndex]);

  const getTeamsByRounds = (section) => {
    const rounds = [];
    for (let i = 0; i < event.gameFormat.rounds; i++) {
      const teams = section.teams.filter((t) => t.round === i + 1);

      if (teams.length > 0) {
        rounds.push({ teams, round: i + 1 });
      }
    }

    return rounds;
  };

  return (
    <Box display={'flex'} flexDirection={'column'} alignItems={'center'}>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'center'}
        alignItems={'center'}
        gap={2}
        m={2}>
        <Typography variant="h5">Scores</Typography>
      </Box>

      {loadingLeaderboard || loading ? (
        <BSCircularProgress caption={'Loading...'} size={30} />
      ) : (
        (sectionsLeaderboard || event.specialty) && (
          <Box sx={{ width: '100%' }}>
            {linkedEvents.length > 0 && (
              <Box display={'flex'} justifyContent={'center'} gap={2} py={1}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => navigate(`/embed/event/${event.id}`)}>
                  {getGameType(event.gameFormat.gameType)}
                </Button>
                {linkedEvents.map((linkedEvent) => (
                  <Button
                    key={linkedEvent.id}
                    variant={'outlined'}
                    color="primary"
                    size="small"
                    onClick={() => navigate(`/embed/event/${linkedEvent.id}`)}>
                    {getGameType(linkedEvent.gameFormat.gameType)}
                  </Button>
                ))}
              </Box>
            )}

            <EmbeddedTabButtons
              tabs={tabs}
              value={selectedTab}
              onChange={setSelectedTab}
              ariaLabel="Scores views"
            />

            <CustomTabPanel value={selectedTab} index={0}>
              <Box
                sx={{ width: '100%' }}
                display={'flex'}
                flexDirection={'row'}
                gap={3}
                justifyContent={'center'}
                flexWrap={'wrap'}
                py={2}>
                <Stack>
                  <EmbeddedActiveGames event={event.id} />
                </Stack>
              </Box>
            </CustomTabPanel>
            {hasBrackets && (
              <CustomTabPanel value={selectedTab} index={bracketGamesTabIndex}>
                <Box
                  sx={{ width: '100%' }}
                  display={'flex'}
                  flexDirection={'row'}
                  gap={3}
                  justifyContent={'center'}
                  flexWrap={'wrap'}
                  py={2}>
                  <Stack>
                    <EmbeddedBracketGames event={event.id} />
                  </Stack>
                </Box>
              </CustomTabPanel>
            )}
            {hasBrackets && (
              <CustomTabPanel value={selectedTab} index={bracketTabIndex}>
                <Box
                  sx={{ width: '100%' }}
                  display={'flex'}
                  flexDirection={'row'}
                  gap={3}
                  justifyContent={'center'}
                  flexWrap={'wrap'}
                  py={2}>
                  {/* <SingleBracket bracket={event.bracket} /> */}
                  {primaryBracket && <SingleBracket bracket={primaryBracket} event={event} />}
                </Box>
              </CustomTabPanel>
            )}
            {showLogTab && (
              <CustomTabPanel value={selectedTab} index={logTabIndex}>
                <Box sx={{ width: '100%', px: { xs: 1, sm: 2 }, pb: 10 }}>
                  <EmbeddedFindMe
                    entries={findMeEntries}
                    located={located}
                    onLocate={handleFindMeLocate}
                    onClear={handleFindMeClear}
                  />
                  <Box
                    sx={{ width: '100%' }}
                    display={'flex'}
                    flexDirection={'row'}
                    gap={3}
                    justifyContent={'center'}
                    flexWrap={'wrap'}
                    py={2}>
                    {sectionsLeaderboard.map((section, index) => (
                      <Fragment key={index}>
                        <SectionTotalsCard
                          section={section}
                          showReserves
                          focusSectionId={located?.sectionId || null}
                          focusTeamId={located?.teamId || null}
                          focusPlayerKey={located?.playerKey || null}
                          focusToken={located ? findMeTarget?.token || 0 : 0}
                        />
                      </Fragment>
                    ))}
                  </Box>
                </Box>
              </CustomTabPanel>
            )}
            {showScorecardTab && (
              <CustomTabPanel value={selectedTab} index={scorecardTabIndex}>
                {sectionsLeaderboard.map((section) => (
                  <Box
                    key={section.id}
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}>
                    <Box display={'flex'} alignItems={'baseline'} gap={1} my={2}>
                      <Typography variant="h5">{section.sectionName}</Typography>
                      {section.venue && (
                        <Typography variant="subtitle1" color="text.secondary">
                          {section.venue}
                        </Typography>
                      )}
                    </Box>
                    <Box
                      display={'flex'}
                      flexDirection={'row'}
                      justifyContent={'center'}
                      gap={1}
                      flexWrap={'wrap'}>
                      {(() => {
                        const teamRounds = getTeamsByRounds(section);

                        if (teamRounds.length === 0) {
                          return (
                            <Typography>No data available yet, please check back later.</Typography>
                          );
                        } else {
                          return teamRounds.map((tr, index) => (
                            <Fragment key={index}>
                              <SectionScoreCard
                                section={section}
                                teams={tr.teams}
                                round={tr.round}
                              />
                            </Fragment>
                          ));
                        }
                      })()}
                    </Box>
                  </Box>
                ))}
              </CustomTabPanel>
            )}

            {/* <CustomTabPanel value={selectedTab} index={2}>
              <Box
                sx={{ width: '100%' }}
                display={'flex'}
                flexDirection={'row'}
                gap={3}
                justifyContent={'center'}
                flexWrap={'wrap'}
                py={2}>
                <Stack>
                  {event.sections.map((section) => (
                    <TableContainer
                      key={section.id}
                      component={Paper}
                      sx={{
                        m: 2,
                        borderRadius: 3,
                        minWidth: 300,
                        border: '1px solid',
                        borderColor: 'primary.main'
                      }}>
                      <Table padding="none">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell padding="normal" sx={{ fontWeight: 'bold' }}>
                              <Typography variant="body1">{section.name}</Typography>
                            </StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <StyledTableCell padding="normal" align="right">
                              <Button
                                variant="contained"
                                color="info"
                                onClick={() => navigate(`/embed/games/${section.id}`)}
                                fullWidth>
                                View Games
                              </Button>
                            </StyledTableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ))}
                </Stack>
              </Box>
            </CustomTabPanel> */}
          </Box>
        )
      )}
      <PoweredBy />
    </Box>
  );
  // return (
  //   <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
  //     {loading ? (
  //       <Typography>Loading...</Typography>
  //     ) : (
  //       <>
  //         <Typography variant="h4" textAlign="center" mt={2}>
  //           Sections
  //         </Typography>

  //         <Stack>
  //           {event.sections.map((section) => (
  //             <TableContainer
  //               key={section.id}
  //               component={Paper}
  //               sx={{
  //                 m: 2,
  //                 borderRadius: 3,
  //                 maxWidth: 400,
  //                 border: '1px solid',
  //                 borderColor: 'primary.main'
  //               }}>
  //               <Table padding="none">
  //                 <TableHead>
  //                   <TableRow>
  //                     <StyledTableCell padding="normal" sx={{ fontWeight: 'bold' }} colSpan={2}>
  //                       {section.name}
  //                     </StyledTableCell>
  //                   </TableRow>
  //                 </TableHead>
  //                 <TableBody>
  //                   <TableRow>
  //                     <StyledTableCell padding="normal" align="right">
  //                       <Typography>
  //                         Total Games: {section.games ? section.games.length : 0}
  //                       </Typography>
  //                     </StyledTableCell>
  //                     <StyledTableCell padding="normal" align="right">
  //                       <Button
  //                         variant="contained"
  //                         color="info"
  //                         onClick={() => navigate(`/embed/games/${section.id}`)}>
  //                         View Games
  //                       </Button>
  //                     </StyledTableCell>
  //                   </TableRow>
  //                 </TableBody>
  //               </Table>
  //             </TableContainer>
  //           ))}
  //         </Stack>

  //         <PoweredBy />
  //       </>
  //     )}
  //   </Box>
  // );
};

export default EmbeddedDetailedSectionsView;
